/**
 * StationBubble Component
 * SVG count pill on the elevation profile that opens a hover/click popover
 * listing the runners in that bubble (at-station or en-route).
 */

import { useState } from 'react';
import {
  useFloating,
  useHover,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  offset,
  flip,
  shift,
  autoUpdate,
  FloatingPortal,
  safePolygon,
} from '@floating-ui/react';
import type { RunnerSummary } from '../../lib/types';
import { FollowStarV2 } from './FollowStarV2';

export type BubbleKind = 'at_station' | 'enroute';

interface StationBubbleProps {
  cx: number;
  cy: number;
  count: number;
  kind: BubbleKind;
  stationName: string;
  /** For en-route bubbles, the prior station name (e.g. "Homestake → Our Lady"). */
  fromStationName?: string;
  runners: RunnerSummary[];
  followedBibs: Set<string>;
  onToggleFollow: (bib: string) => void;
}

export function StationBubble({
  cx,
  cy,
  count,
  kind,
  stationName,
  fromStationName,
  runners,
  followedBibs,
  onToggleFollow,
}: StationBubbleProps) {
  const [isOpen, setIsOpen] = useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'top',
    strategy: 'fixed',
    middleware: [offset(8), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    mouseOnly: true,
    handleClose: safePolygon(),
    delay: { open: 60, close: 100 },
  });
  const click = useClick(context);
  const dismiss = useDismiss(context, { outsidePress: true });
  const role = useRole(context, { role: 'dialog' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    dismiss,
    role,
  ]);

  const fill = kind === 'at_station' ? 'var(--v2-status-at-station)' : 'var(--v2-status-enroute)';

  // Pill width grows with digit count so 3-digit counts (e.g. "101") fit
  const pillWidth = Math.max(20, 12 + String(count).length * 5);

  return (
    <>
      <g
        ref={refs.setReference}
        {...getReferenceProps()}
        style={{ cursor: 'pointer' }}
      >
        <rect
          x={cx - pillWidth / 2}
          y={cy - 22}
          width={pillWidth}
          height={14}
          rx={7}
          fill={fill}
        />
        <text
          x={cx}
          y={cy - 12}
          textAnchor="middle"
          fill="#FFFFFF"
          fontSize="8"
          fontWeight="700"
          fontFamily="Inter"
          style={{ pointerEvents: 'none' }}
        >
          {count}
        </text>
      </g>

      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            {...getFloatingProps()}
            className="v2-bubble-popover"
          >
            <div className="v2-bubble-popover-header">
              <div className="v2-bubble-popover-kind">
                {kind === 'at_station' ? 'AT' : 'EN ROUTE TO'}
              </div>
              <div className="v2-bubble-popover-station">
                {kind === 'enroute' && fromStationName ? (
                  <>
                    <span style={{ opacity: 0.6 }}>{fromStationName} →</span>{' '}
                    {stationName}
                  </>
                ) : (
                  stationName
                )}
              </div>
              <div className="v2-bubble-popover-count">
                {count} {count === 1 ? 'runner' : 'runners'}
              </div>
            </div>
            <div className="v2-bubble-popover-list">
              {runners.map((runner) => {
                const isFollowed = followedBibs.has(runner.bib);
                const displayName = runner.is_anonymous
                  ? 'Anonymous'
                  : `${runner.first_name} ${runner.last_name}`;
                return (
                  <div key={runner.id} className="v2-bubble-popover-row">
                    <div className="v2-bubble-popover-bib">{runner.bib}</div>
                    <div className="v2-bubble-popover-name">{displayName}</div>
                    <FollowStarV2
                      isFollowed={isFollowed}
                      onToggle={() => onToggleFollow(runner.bib)}
                      size={16}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
